import {
  CompanionRole,
  getCompanionControllerFindOneQueryKey,
  getCompanionControllerFindUserQueryKey,
  useCompanionControllerFindOne,
  useCompanionControllerFindUser,
} from '@/api';
import type { ReadonlyRefOrGetter } from '@vueuse/core';
import { defineStore } from 'pinia';
import { computed, ref, watch, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import { usePiniaRefStore } from '../../composables/usePiniaRefStore';
import { useResponseData } from '../../composables/useResponseData';
import { useCompanionSse } from '../../stores/companionSseStore';
import { registerLoading } from '../../stores/loadingStore';
import { useCompanionAuth } from './companionAuthStore';

/** Roles the owning user is exposed to - an offer display gets a 403 instead. */
const USER_ROLES: CompanionRole[] = [
  CompanionRole.FILE_SYNC,
  CompanionRole.BARCODE_SCANNER,
  CompanionRole.OFFER_CREATE,
];

const useCompanionSessionStore = defineStore('companion', () => {
  const router = useRouter();
  const { clearSession } = useCompanionAuth();

  const companionId = ref<string>('');

  const {
    data,
    refetch,
    isFetching: isFetchingCompanion,
  } = useCompanionControllerFindOne(companionId, {
    query: {
      enabled: computed(() => !!companionId.value),
      queryKey: getCompanionControllerFindOneQueryKey(companionId),
    },
  });
  const companion = useResponseData(data);

  const hasUser = computed(
    () => !!companion.value?.roles.some((role) => USER_ROLES.includes(role)),
  );

  const { data: userData, isFetching: isFetchingUser } = useCompanionControllerFindUser(
    companionId,
    {
      query: {
        enabled: hasUser,
        queryKey: getCompanionControllerFindUserQueryKey(companionId),
      },
    },
  );
  const user = useResponseData(userData);

  /** Who the session belongs to, for "open {name}'s desktop" - null until known. */
  const userName = computed(() => user.value?.displayName || user.value?.username || null);

  // The store outlives every component, so it registers directly instead of
  // going through `watchLoading` - there is no component unmount to clean up on.
  registerLoading(isFetchingCompanion, isFetchingUser);

  /** Presence as last reported by the stream; `null` until the first event. */
  const streamDesktopConnected = ref<boolean | null>(null);

  watch(companionId, () => (streamDesktopConnected.value = null));

  // One stream for the whole session, whatever its roles: presence rides on it
  // for every role, and feature composables subscribing to the same id share
  // it rather than opening their own - the server caps streams per session.
  const { connectionStatus } = useCompanionSse(companionId, {
    global: true,
    onConnected: ({ desktopConnected }) => (streamDesktopConnected.value = desktopConnected),
    onPresenceChanged: ({ desktopConnected }) => (streamDesktopConnected.value = desktopConnected),
    // Expired or ended on the desktop: the tokens are dead with it, and only a
    // fresh QR code gets this device back in.
    onGone: () => {
      clearSession();
      router.replace({ name: 'companionSessionEnded' });
    },
  });

  /**
   * Whether a desktop client holds the session open. Seeded from the session
   * itself, then kept current by the stream.
   */
  const desktopConnected = computed(
    () =>
      connectionStatus.value !== 'gone' &&
      (streamDesktopConnected.value ?? companion.value?.desktopConnected ?? false),
  );

  return {
    companionId,
    companion,
    user,
    userName,
    connectionStatus,
    desktopConnected,
    refetch,
  };
});

export const useCompanionSession = (
  withCompanionId?: ReadonlyRefOrGetter<string> | Ref<string> | null,
) => {
  const store = usePiniaRefStore(useCompanionSessionStore());

  if (withCompanionId) {
    watch(withCompanionId, (newCompanionId) => (store.companionId.value = newCompanionId), {
      immediate: true,
    });
  }

  return store;
};
